import React from 'react';
import { useNavigate } from 'react-router-dom';
import DateRangePicker from '../components/DateRangePicker';
import { usePortfolio } from '../context/PortfolioContext';
import { useAuth } from '../context/AuthContext';
import PageOnboarding from '../components/PageOnboarding';

const OPTIMIZE_ONBOARDING_STEPS = [
  {
    title: 'Configure Your Optimization',
    message: 'Set your investment goals and constraints. Choose what matters most - higher returns, lower risk, or a balanced approach.',
  },
  {
    title: 'Optimization Objective',
    message: 'Sharpe Ratio balances risk and return. Choose "Maximize Returns" for aggressive growth or "Minimize Risk" for conservative investing.',
    target: '.optimization-params',
  },
  {
    title: 'Run Optimization',
    message: 'Click "Optimize Portfolio" when ready. Our AI will calculate the ideal allocation percentages for your selected funds.',
    target: '.btn-optimize',
  },
];

const OptimizePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    dateRange,
    setDateRange,
    optimizationParams,
    setOptimizationParams,
    handleConstraintChange,
    handleOptimize,
    navData,
    loading,
  } = usePortfolio();

  return (
    <div className="container page-container">
      <div className="page-header">
        <h2>Step 2: Optimization Parameters</h2>
        <p>Configure your portfolio optimization settings</p>
      </div>

      <section className="section">
        <h3>Date Range (Optional)</h3>
        <DateRangePicker
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          navData={navData}
        />
      </section>

      <section className="section">
        <h3>Optimization Settings</h3>
        <div className="optimization-params">
          <div className="param-group">
            <label>Optimization Objective:</label>
            <select
              value={optimizationParams.objective}
              onChange={(e) => setOptimizationParams(prev => ({
                ...prev,
                objective: e.target.value
              }))}
            >
              <option value="sharpe">Maximize Sharpe Ratio</option>
              <option value="max_return">Maximize Returns</option>
              <option value="min_volatility">Minimize Risk</option>
            </select>
          </div>

          <div className="param-group">
            <label>
              Max Allocation per Fund: {(optimizationParams.constraints.max_allocation * 100).toFixed(0)}%
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={optimizationParams.constraints.max_allocation}
              onChange={(e) => handleConstraintChange('max_allocation', parseFloat(e.target.value))}
            />
          </div>

          <div className="param-group">
            <label>
              Min Allocation per Fund: {(optimizationParams.constraints.min_allocation * 100).toFixed(0)}%
            </label>
            <input
              type="range"
              min="0"
              max="0.2"
              step="0.01"
              value={optimizationParams.constraints.min_allocation}
              onChange={(e) => handleConstraintChange('min_allocation', parseFloat(e.target.value))}
            />
          </div>
        </div>
      </section>

      <div className="page-actions">
        <button
          className="btn-back"
          onClick={() => navigate('/select')}
        >
          ← Back to Fund Selection
        </button>
        <button
          className="btn-optimize"
          onClick={handleOptimize}
          disabled={loading}
        >
          {loading ? 'Optimizing...' : 'Optimize Portfolio'}
        </button>
      </div>

      <PageOnboarding
        pageId="optimize"
        userEmail={user?.email}
        steps={OPTIMIZE_ONBOARDING_STEPS}
      />
    </div>
  );
};

export default OptimizePage;
